<template lang="pug">
  div

    v-snackbar(:value="recipientsError" color="error")
      div {{ recipientsErrorMessage }}

    pagination-list-modal(
      v-if="showContactsDialog"
      v-model="showContactsDialog"
      :items="selectedValues.slice(skip, skip + limit)"
      :total="selectedValues.length"
      :limit="limit"
      :skip="skip"
      title="SMS Recipients"
      description="These contacts will receive the SMS:"
      @close="closeContactsDialog"
      @setPage="setContactsPage"
      @setPageLimit="setContactsPageLimit")
        template(slot="items" slot-scope="{ item }")
          v-layout(row)
            v-checkbox.pa-0.ma-0(:input-value="true" color='primary' @click="removeItem(item)" hide-details :style="{'flex':0}") 
            div
              span.pr-1 {{displayContact(item)}}
              v-icon(small :style = "styleFromStatus(item.opt_in_status)") {{iconFromStatus(item.opt_in_status)}}


    v-container
      v-layout(row)
        v-flex(xs12)
          v-autocomplete(
            v-model="selectedValues"
            label="To:"
            :items="contacts"
            :filter="filter"
            :search-input.sync="toSearchValue"
            :item-text="displayContact"
            :disabled="shouldDisableSMSRecipients"
            :multiple="hasBulkTextsAccess"
            item-value="id"
            prepend-icon="person"
            dense
            clearable
            return-object
            chips
            deletable-chips
          )
            template(slot="append-outer")
              v-tooltip(top)
                span The Opt In status of your contacts appears on the recipient list, any contacts that have opted out of text messages will be excluded and will not receive your SMS text.
                v-icon.pl-2(slot="activator") help_outline
            template(slot="no-data")
              v-list-tile
                v-list-tile-content
                  v-list-tile-title(v-if="!toSearchValue") {{ displayNone() }}
                  v-list-tile-title(v-else) {{ displayNone(toSearchValue) }}
            template(slot="item" slot-scope="data")
              v-checkbox(v-if="hasBulkTextsAccess" :input-value="isSelected(data.item)" :style="{'flex':0}" color='primary')
              div {{ displayContact(data.item) }}
              v-icon.pl-3(:style = "styleFromStatus(data.item.opt_in_status)") {{iconFromStatus(data.item.opt_in_status)}}
            template(slot="selection" slot-scope="data")
              v-chip(v-if="data.index < 5" :style="chipStyleFromStatus(data.item.opt_in_status)") {{displayContact(data.item)}}
                v-icon(right :style = "styleFromStatus(data.item.opt_in_status)") {{iconFromStatus(data.item.opt_in_status)}}
              v-chip.clickable(v-if="data.index === 5" @click.stop="openContactsDialog") + {{ invisibleContacts.length }} more
          p.ml-2.red--text.caption.font-weight-bold(v-if="shouldDisplayNoPhoneMsg") It looks like a contact does not have a phone number. Please update the contact information to send a text message.
</template>
<script>
import { createNamespacedHelpers, mapState } from 'vuex';
import { debounce } from '@/Core/utilities';
import { hasMoreItems } from '@/Core/utilities/arrayComparison';
import { COMMUNICATION_TYPE } from '@/Core/config/communicationType';
import {
  ES_STATUS,
  STATUS_ICON,
  STATUS_COLOR
} from '@/Contacts/config/profile-optin-status'
import PaginationListModal from '@/Core/components/PaginationListModal'
import { LdFeatures } from 'lion-vue';

const { mapState: mapRecipientsState, mapActions: mapRecipientsActions } = createNamespacedHelpers(
  'recipients',
);
const { mapGetters: mapUserInformationGetters } = createNamespacedHelpers(
  'userInformation',
);

export default {
  data() {
    return {
      selectedValues: [],
      toSearchValue: '',
      showContactsDialog: false,
      limit: 15,
      skip: 0,
      page: 1,
      shouldDisplayNoPhoneMsg: false,
    };
  },

  components: {
    PaginationListModal
  },

  computed: {
    ...mapRecipientsState([
      'allContacts',
      'toSelected',
      'searchString',
      'recipientsError',
      'recipientsErrorMessage',
      'recipientsInProgress',
    ]),
    ...mapUserInformationGetters(['userHasFeatureFlag', 'hasAccessToFeature']),
    ...mapState({ selectedActionType: state => state.contactsTable.selectedActionType }),
    visibleContacts() {
      return this.toSelected.slice(0, 5);
    },
    invisibleContacts() {
      return this.toSelected.slice(5);
    },
    contacts() {
      this.shouldDisplayNoPhoneMsg = this.toSelected.length && this.toSelected.some(contact => !contact.mobile_phone);
      
      return this.allContacts.concat(this.toSelected);
    },
    allContactsSelected() {
      return this.selectedActionType === 'all';
    },
    shouldDisableSMSRecipients() {
      return this.userHasFeatureFlag('comms-bulk-actions') && this.allContactsSelected;
    },
    hasBulkTextsAccess() {
      return this.hasAccessToFeature(LdFeatures.BulkTexts);
    },
  },

  created() {
    this.clearErrors();
    // Assigns the initial selected contacts if present
    if (this.toSelected && this.toSelected.length) {
      this.selectedValues = this.hasBulkTextsAccess ? this.toSelected : this.toSelected[0];
    }
  },

  watch: {
    /**
     * Changes the search value on the backend to be queried upon
     */
    toSearchValue(value) {
      if (value && value.length > 2) {
        debounce(this.setSearchString, [this.formatSearchString(value)]);
      } else {
        debounce(this.setSearchString, [this.formatSearchString('')]);
      }
    },
    /**
     * Stores the resired recipients on the Vuex store
     */
    selectedValues(currentRecipients, prevRecipients) {
      if (!this.hasBulkTextsAccess) {
        this.toSearchValue = '';
        const newValue = !currentRecipients ? [] : [currentRecipients];
        this.setToSelected(newValue);
        return;
      }

      // Clears the search value if there a new tag being added
      // Otherwise, it means that the user is removing a selected tag,
      // at which don't clear the search value
      if (!hasMoreItems(prevRecipients, currentRecipients)) this.toSearchValue = '';
      this.setToSelected(currentRecipients);
    },
  },
  methods: {
    ...mapRecipientsActions([
      'getContactsAndUsers',
      'setSearchString',
      'setToSelected',
      'clearErrors',
    ]),
    /**
     * Parses the serach value string into an object that the recipients store needs
     * @param {String} searchValue is the intended search value
     * @returns {Object} the formatted searchValue object to be given to the vuex store
     */
    formatSearchString(searchValue) {
      return {
        searchValue,
        type: COMMUNICATION_TYPE.TEXT,
      };
    },
    /**
     * Sets the desired display value for each contact
     */
    displayContact: item => {
      const phone = item.mobile_phone;
      if (item.first_name && item.last_name) {
        return `${item.first_name || ''} ${item.last_name || ''} (${phone})`;
      }
      return phone;
    },
    /**
     * @param {Object} [item] the current item we're iterating on
     * @param {String} [queryText] The search string that the user inputs
     * @param {String} [itemText] The stringified item
     *
     * @returns {Boolean}  It returns whether each item should be included
     *                     the resulting list
     *
     * Custom front-end filter in order to filter out the people with sent texts.
     * This is necessary because we combine the selected contacts to the all contacts.
     * Without the combination, the selected items will not be visually present.
     */
    filter(item, queryText, itemText) {
      if (item.header) return false;

      const text = itemText || '';
      const query = queryText || '';

      return text.toString().toLowerCase().indexOf(query.toString().toLowerCase()) > -1;
    },
    /**
     * Returns the correct string if there is no contact that matches
     * the given search string.
     */
    displayNone() {
      if (this.toSearchValue && this.toSearchValue.length > 2) {
        return `No results matching ${this.toSearchValue}.`;
      }
      return 'Please enter at least 3 letters to search contacts';
    },
    styleFromStatus(contactOptInStatus) {
      if (contactOptInStatus === ES_STATUS.approved) return { color: STATUS_COLOR.approved }
      if (contactOptInStatus === ES_STATUS.denied) return { color: STATUS_COLOR.denied }
      return { color: STATUS_COLOR.pending }
    },
    iconFromStatus(contactOptInStatus) {
      if (contactOptInStatus === ES_STATUS.approved) return STATUS_ICON.approved
      if (contactOptInStatus === ES_STATUS.denied) return STATUS_ICON.denied
      return STATUS_ICON.pending
    },
    chipStyleFromStatus(contactOptInStatus) {
      if (contactOptInStatus === ES_STATUS.denied) {
        return { opacity: '0.6' }
      }
    },
    openContactsDialog() {
      this.showContactsDialog = true;
    },
    closeContactsDialog() {
      this.showContactsDialog = false;
    },
    setContactsPage(page) {
      this.page = page;
      this.skip = (page - 1) * this.limit;
    },
    setContactsPageLimit(limit) {
      this.limit = limit;
      this.setContactsPage(1)
    },
    isSelected(item) {
      return this.selectedValues.map(value => value.id).includes(item.id);
    },
    removeItem(item) {
      const index = this.selectedValues.findIndex(selectedItem => selectedItem.id === item.id);
      if (index !== -1) {
        this.selectedValues = [
        ...this.selectedValues.slice(0, index),
        ...this.selectedValues.slice(index + 1),
      ];
      }
    },
  },
};
</script>
<style scoped lang="scss">
@media only screen and (min-width: 1904px) {
  .container {
    max-width: 95%;
  }
}

.clickable /deep/ .v-chip__content {
  cursor: pointer !important;
  color: var(--v-primary-base);
}
</style>